import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  Layout,
  Result,
  Select,
  Space,
  Spin,
  Typography,
  Upload,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  publicFetchJson,
  publicPostJson,
  publicPostMultipart,
} from "../../utils/apiHelper";
import {
  getPublicFormConfigUrl,
  getPublicFormNoticeUrl,
  getPublicFormTokenUrl,
} from "../../redux/actions/formsAction";
import { useThemedLogo } from "../../theme/useThemedLogo";
import { validateClientFormFields } from "../App/Forms/formUtils";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const BRAND_FOOTER =
  "This form is powered by Mailinflow Forms — a simple way to collect leads and stay in touch.";

function toFormData(payload) {
  const formData = new FormData();
  for (const [key, value] of Object.entries(payload)) {
    if (value === undefined || value === null) continue;
    if (value instanceof File) {
      formData.append(key, value, value.name);
    } else if (typeof value === "boolean") {
      formData.append(key, value ? "true" : "false");
    } else {
      formData.append(key, value);
    }
  }
  return formData;
}

function loadRecaptchaScript(siteKey) {
  return new Promise((resolve, reject) => {
    if (!siteKey) {
      resolve();
      return;
    }
    if (window.grecaptcha) {
      resolve();
      return;
    }
    const existing = document.getElementById("mailinflow-recaptcha");
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    script.id = "mailinflow-recaptcha";
    script.src = `https://www.google.com/recaptcha/api.js?render=explicit`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

function HostedField({ field }) {
  const rules = field.required
    ? [{ required: true, message: `${field.label} is required` }]
    : [];

  if (field.type === "textarea") {
    return (
      <Form.Item name={field.name} label={field.label} rules={rules}>
        <TextArea rows={4} placeholder={field.placeholder} />
      </Form.Item>
    );
  }

  if (field.type === "select") {
    return (
      <Form.Item name={field.name} label={field.label} rules={rules}>
        <Select
          placeholder={field.placeholder || `Select ${field.label}`}
          options={(field.options || []).map((opt) => ({
            value: opt.value,
            label: opt.label,
          }))}
          allowClear
        />
      </Form.Item>
    );
  }

  if (field.type === "checkbox") {
    return (
      <Form.Item
        name={field.name}
        valuePropName="checked"
        rules={
          field.required
            ? [
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(`${field.label} must be checked`),
                        ),
                },
              ]
            : []
        }
      >
        <Checkbox>{field.label}</Checkbox>
      </Form.Item>
    );
  }

  // FR-3.6: rendered unticked, no required rule regardless of what's
  // stored on the field -- a DPDP consent checkbox must never block
  // submission, initialValue={false} is belt-and-suspenders alongside the
  // API-side enforcement (lib/forms/fieldSchema.js).
  if (field.type === "consent_marketing") {
    return (
      <Form.Item name={field.name} valuePropName="checked" initialValue={false}>
        <Checkbox>{field.label}</Checkbox>
      </Form.Item>
    );
  }

  if (field.type === "file") {
    return (
      <Form.Item
        name={field.name}
        label={field.label}
        valuePropName="fileList"
        getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
        rules={rules}
      >
        <Upload
          beforeUpload={() => false}
          maxCount={1}
          accept={field.acceptedMimeTypes?.length ? field.acceptedMimeTypes.join(",") : undefined}
        >
          <Button icon={<UploadOutlined />}>Choose file</Button>
        </Upload>
      </Form.Item>
    );
  }

  const inputType =
    field.type === "email"
      ? "email"
      : field.type === "phone"
        ? "tel"
        : "text";

  return (
    <Form.Item name={field.name} label={field.label} rules={rules}>
      <Input type={inputType} placeholder={field.placeholder} />
    </Form.Item>
  );
}

export default function HostedForm() {
  const { publicId } = useParams();
  const navigate = useNavigate();
  const logo = useThemedLogo();
  const [form] = Form.useForm();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [antiSpamToken, setAntiSpamToken] = useState("");
  const recaptchaRef = useRef(null);
  const recaptchaWidgetId = useRef(null);
  // The honeypot is a plain hidden input, not an antd Form.Item, so its
  // value never reached onFinish(values) and the API's honeypot check
  // (POST /f/:publicId, settings.honeypotField) had nothing to look at. It
  // is read through this ref and sent with the submission.
  const honeypotRef = useRef(null);
  const honeypotField = config?.honeypotField || "_gotcha";

  const loadToken = useCallback(() => {
    return axios
      .get(getPublicFormTokenUrl(publicId), {
        headers: { Accept: "application/json" },
      })
      .then((res) => {
        if (res.data?.token) setAntiSpamToken(res.data.token);
      })
      .catch(() => {});
  }, [publicId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    setNotFound(false);

    publicFetchJson(getPublicFormConfigUrl(publicId))
      .then(async (res) => {
        if (cancelled) return;
        const data = res.data;
        setConfig(data);
        await loadToken();
        if (data.recaptcha?.enabled && data.recaptcha.siteKey) {
          await loadRecaptchaScript(data.recaptcha.siteKey);
          if (!cancelled && window.grecaptcha && recaptchaRef.current) {
            recaptchaWidgetId.current = window.grecaptcha.render(
              recaptchaRef.current,
              { sitekey: data.recaptcha.siteKey },
            );
          }
        }
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.response?.status === 404) {
          setNotFound(true);
        } else {
          setError(
            err.response?.data?.error ||
              "This form could not be loaded. Try again later.",
          );
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [publicId, loadToken]);

  const onFinish = async (values) => {
    setSubmitting(true);
    setError("");

    const fileFields = (config.fields || []).filter((f) => f.type === "file");
    const normalizedValues = { ...values };
    for (const f of fileFields) {
      const fileList = values[f.name];
      normalizedValues[f.name] = Array.isArray(fileList)
        ? fileList[0]?.originFileObj
        : undefined;
    }

    const clientCheck = validateClientFormFields(
      config.fields,
      config.validation,
      normalizedValues,
    );
    if (!clientCheck.ok) {
      setError(clientCheck.errors[0]?.message || "Please check the form.");
      setSubmitting(false);
      return;
    }

    const payload = { ...normalizedValues, antiSpamToken };
    const honeypotValue = honeypotRef.current?.value || "";
    if (honeypotValue) payload[honeypotField] = honeypotValue;
    if (config.recaptcha?.enabled) {
      const token = window.grecaptcha?.getResponse(recaptchaWidgetId.current);
      if (!token) {
        setError("Please complete the captcha.");
        setSubmitting(false);
        return;
      }
      payload["g-recaptcha-response"] = token;
    }

    const hasFile = fileFields.some((f) => normalizedValues[f.name]);

    try {
      const res = hasFile
        ? await publicPostMultipart(`/f/${publicId}`, toFormData(payload))
        : await publicPostJson(`/f/${publicId}`, payload);
      if (res.data?.ok) {
        const redirect = config.redirectUrl?.trim();
        if (redirect) {
          window.location.href = redirect;
          return;
        }
        const params = new URLSearchParams({ received: "1" });
        if (config.consentMode === "double") {
          params.set("pending_confirm", "1");
        }
        navigate(`/forms/thank-you?${params.toString()}`);
        return;
      }
      setError(res.data?.error || "Submission failed.");
    } catch (err) {
      const data = err.response?.data;
      setError(
        data?.error ||
          data?.errors?.[0]?.message ||
          "Something went wrong. Please try again.",
      );
      if (window.grecaptcha && recaptchaWidgetId.current != null) {
        window.grecaptcha.reset(recaptchaWidgetId.current);
      }
      await loadToken();
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout style={{ minHeight: "100vh", background: "#f5f7f8" }}>
        <div style={{ textAlign: "center", paddingTop: "40vh" }}>
          <Spin size="large" />
        </div>
      </Layout>
    );
  }

  if (notFound) {
    return (
      <Layout style={{ minHeight: "100vh", background: "#f5f7f8" }}>
        <Result
          status="404"
          title="Form not found"
          subTitle="This form does not exist or is no longer available."
        />
      </Layout>
    );
  }

  if (!config) {
    return (
      <Layout style={{ minHeight: "100vh", background: "#f5f7f8" }}>
        <Result
          status="error"
          title="Unable to load form"
          subTitle={error || "Please try again later."}
        />
      </Layout>
    );
  }

  const sortedFields = [...(config.fields || [])].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  );

  return (
    <Layout style={{ minHeight: "100vh", background: "#f5f7f8" }}>
      <Space
        direction="vertical"
        align="center"
        style={{
          display: "flex",
          width: "100%",
          minHeight: "100vh",
          justifyContent: "center",
          padding: "32px 24px",
        }}
      >
        <img
          src={logo}
          alt="Mailinflow"
          height={28}
          style={{ display: "block", marginBottom: 20 }}
        />
        <div
          style={{
            maxWidth: 520,
            width: "100%",
            background: "#fff",
            borderRadius: 12,
            padding: "32px 28px 28px",
            boxShadow: "0 8px 24px rgba(15, 20, 25, 0.06)",
            border: "1px solid #d8e0e3",
          }}
        >
          <Title level={4} style={{ marginTop: 0, marginBottom: 4 }}>
            {config.name || "Contact us"}
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 20 }}>
            Fill in the form below and we will get back to you.
          </Paragraph>

          {error && (
            <Alert
              type="error"
              message={error}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Form form={form} layout="vertical" onFinish={onFinish}>
            {sortedFields.map((field) => (
              <HostedField key={field.id || field.name} field={field} />
            ))}

            <input
              ref={honeypotRef}
              type="text"
              name={honeypotField}
              style={{ display: "none" }}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />

            {config.recaptcha?.enabled && config.recaptcha.siteKey && (
              <Form.Item>
                <div ref={recaptchaRef} />
              </Form.Item>
            )}

            <Form.Item style={{ marginBottom: 8 }}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={submitting}
              >
                Submit
              </Button>
            </Form.Item>
          </Form>

          {config.hasPublishedDpdpNotice && (
            <Text
              type="secondary"
              style={{ fontSize: 12, display: "block", textAlign: "center", marginBottom: 4 }}
            >
              <a
                href={getPublicFormNoticeUrl(publicId)}
                target="_blank"
                rel="noreferrer"
              >
                Notice: how we use your data
              </a>
            </Text>
          )}
          <Text
            type="secondary"
            style={{ fontSize: 12, display: "block", textAlign: "center" }}
          >
            {BRAND_FOOTER}
          </Text>
        </div>
      </Space>
    </Layout>
  );
}
